import type { RepeatableColumn } from '@/api/workflows';

import { type EmailVerifyLookup, emailValueVerdict } from './emailVerifyGate';
import { runValidate, validateDateRules } from './fieldValidation';
import { columnLengthViolation } from './lengthRule';
import { columnNumberRangeViolation } from './numberRange';
import { resolveValidationMessage, type MessageOwner } from './validationMessage';

/** One cell's broken rule: an i18n key (+ params). The column supplies any authored wording. */
export type ColumnCellViolation = {
  errorKey: string;
  params?: Record<string, string | number>;
  /**
   * A violation that holds Next but says nothing beneath the cell.
   *
   * There is exactly one: an email whose ZeroBounce answer has not arrived yet. It lasts a few
   * hundred milliseconds and is nobody's to fix, so a message would appear and vanish before it
   * could be read — but Next must still be held for that window, because it is precisely the gap
   * a fast clicker would otherwise walk through. Callers that render must check this; callers
   * that gate must not.
   */
  silent?: boolean;
};

type CellRuleColumn = Pick<
  RepeatableColumn,
  'type' | 'minLength' | 'maxLength' | 'min' | 'max' | 'validate' | 'regexPattern' | 'noPastDates'
>;

/**
 * Every authored rule on one FILLED cell of a RepeatableRow column — the single check the cell's
 * red text and FormShell's held Next both call, so the two cannot disagree. First failure wins,
 * in the scalar field's order (format rule, then length):
 *
 *  - Date column: `noPastDates` (QA DP-021 — the PAC Funds "Start date" took a past date), via
 *    the same `validateDateRules` a Date field uses;
 *  - Number column: the authored `min` / `max` figures (a fund's minimum investment, a cap on
 *    an amount) — see `numberRange.ts` for why they are checked here instead of bounding the
 *    input;
 *  - text column: the `regex` rule (QA INV-018 — "Wire order number" took `W  O`) and the
 *    `email` rule, via the same `runValidate` a text field uses, then min / max length.
 *
 * `sin` / `phone` / `postal` are still NOT run from a column's `validate`, and that is not an
 * oversight: each of those three is half a rule here, because the other half is the input mask
 * `applyFormatMask` puts on a scalar field and a column has none. Enforcing "9 digits" on a cell
 * that never groups them as the advisor types would be a message with no matching behaviour.
 * `email` has no mask to be missing, which is why it could be turned on (with ZeroBounce) while
 * the other three wait for a decision about masking cells.
 *
 * Blank passes — `required` owns an empty cell.
 */
export function columnCellViolation(
  col: CellRuleColumn,
  value: unknown,
  today?: Date,
  /** ZeroBounce verdicts, when this runtime may verify. See `emailVerifyGate.ts`; absent, an
   *  `email` column is held to its syntax rule alone, exactly as it is in the public runtime. */
  emailLookup?: EmailVerifyLookup,
): ColumnCellViolation | null {
  if (col.type === 'Date') {
    if (!col.noPastDates || typeof value !== 'string') return null;
    return validateDateRules({ noPastDates: true }, value, { today });
  }
  if (col.type === 'Number') return columnNumberRangeViolation(col, value);
  if (col.type !== 'ShortText' && col.type !== 'LongText') return null;
  if (typeof value !== 'string') return null;
  if (col.validate === 'regex') {
    const format = runValidate('regex', value, col.regexPattern);
    if (format) return format;
  }
  if (col.validate === 'email') {
    const format = runValidate('email', value);
    if (format) return format;
    // Syntax passed, so a verdict is meaningful. See `silent` above for the `checking` case.
    const verdict = emailValueVerdict(value, emailLookup);
    if (verdict === 'checking') {
      return { errorKey: 'formRenderer.validation.email.notDeliverable', silent: true };
    }
    if (verdict === 'invalid') return { errorKey: 'formRenderer.validation.email.notDeliverable' };
  }
  return columnLengthViolation(col, value);
}

/**
 * The message for a {@link ColumnCellViolation}: the column's authored wording for that rule
 * (its `validationMessages`, or a regex column's `regexMessageEn/Fr`), else the generic copy —
 * the scalar field's lookup, via {@link resolveValidationMessage}.
 */
export function columnCellMessage(
  col: MessageOwner,
  violation: ColumnCellViolation,
  language: 'en' | 'fr',
  t: (key: string, params?: Record<string, string | number>) => string,
): string {
  return resolveValidationMessage(col, violation.errorKey, violation.params, language, t);
}
